import { logErrorToSentry } from "./sentryReporting.js";

const RESIZE_OBSERVER_LOOP = /resizeobserver loop/i;
const EXTENSION_STACK = /^(chrome|moz|safari-web)-extension:\/\//i;

const readStack = (error) =>
  typeof error?.stack === "string" ? error.stack : "";

const isWindowTarget = (target) => {
  if (!target) return false;
  if (typeof window === "undefined") return false;
  return target === window;
};

export const classifyWindowError = (event) => {
  if (!event) return { ignore: true, reason: "empty" };
  if (event.target && !isWindowTarget(event.target)) {
    return { ignore: true, reason: "resource" };
  }

  const message = String(event.message || event.error?.message || "");
  if (RESIZE_OBSERVER_LOOP.test(message)) {
    return { ignore: true, reason: "resize-observer" };
  }
  if (message === "Script error.") {
    return { ignore: true, reason: "cross-origin-script" };
  }
  if (EXTENSION_STACK.test(readStack(event.error))) {
    return { ignore: true, reason: "extension" };
  }

  const error =
    event.error instanceof Error
      ? event.error
      : new Error(message || "window.error");

  return { ignore: false, error };
};

export const classifyUnhandledRejection = (event) => {
  const reason = event?.reason;
  if (!reason) return { ignore: true, reason: "empty" };
  if (reason?.name === "AbortError") {
    return { ignore: true, reason: "abort" };
  }
  if (EXTENSION_STACK.test(readStack(reason))) {
    return { ignore: true, reason: "extension" };
  }

  const error =
    reason instanceof Error
      ? reason
      : new Error(typeof reason === "string" ? reason : "unhandledrejection");

  if (RESIZE_OBSERVER_LOOP.test(error.message || "")) {
    return { ignore: true, reason: "resize-observer" };
  }

  return { ignore: false, error };
};

const reportException = ({ error, sentryMeta }) => {
  logErrorToSentry({
    errorFormatted: {
      message: error?.message || "Unknown error",
      code: error?.code || sentryMeta?.functionName,
    },
    rawError:
      error instanceof Error
        ? error
        : new Error(error?.message || "Unknown error"),
    functionName: sentryMeta?.functionName,
    context: sentryMeta?.context,
    tags: { source: "LaunchError", ...sentryMeta?.tags },
    level: sentryMeta?.level || "error",
    capture: "exception",
  });
};

// Render crashes have no user-facing LaunchError alert. They use the same
// Sentry sink LaunchError uses so a blank crash still emits one event.
export const reportReactRenderError = ({
  error,
  componentStack,
  functionName = "AppErrorBoundary",
} = {}) => {
  const stack =
    typeof componentStack === "string" ? componentStack.slice(0, 2000) : null;
  reportException({
    error,
    sentryMeta: {
      functionName,
      context: { componentStack: stack },
      tags: { source: "react" },
    },
  });
};

export const reportWindowError = (event) => {
  const classified = classifyWindowError(event);
  if (classified.ignore) return false;
  reportException({
    error: classified.error,
    sentryMeta: {
      functionName: "window.onerror",
      tags: { source: "window" },
    },
  });
  return true;
};

export const reportUnhandledRejection = (event) => {
  const classified = classifyUnhandledRejection(event);
  if (classified.ignore) return false;
  reportException({
    error: classified.error,
    sentryMeta: {
      functionName: "unhandledrejection",
      tags: { source: "promise" },
    },
  });
  return true;
};

export const installGlobalErrorHandlers = () => {
  if (typeof window === "undefined") return;
  if (window.__adleGlobalErrorHandlersInstalled) return;
  window.__adleGlobalErrorHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    reportWindowError(event);
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportUnhandledRejection(event);
  });
};
